import React, { FC, Fragment, useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useFormikContext } from 'formik';
import { Button, Divider, Card, CardHeader, CardContent, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { UserGroup } from 'types/app';
import { Loaders } from 'components';
import { getEmbed, isEmbed } from 'features/content/helpers';
import { Image, Provider, Title } from 'features/content/widgets';
import { USER_FEED_ID } from 'constants/index';

import styles from './styles';
import { ContentInput, PreviewUrlMutation, usePreviewUrlMutation } from 'api';
import { useNotify } from 'hooks';
import { RemoveTypes } from '../../types';

const useStyles = makeStyles(styles);

interface IProps {
  onRemove: (type: RemoveTypes) => () => void;
  selectedGroup: UserGroup;
  url: string;
}

const Url: FC<IProps> = ({ onRemove, selectedGroup, url }) => {
  const classes = useStyles();
  const notify = useNotify();
  const { values, setFieldValue } = useFormikContext<ContentInput>();

  useEffect(() => {
    const groupId = selectedGroup?.id || USER_FEED_ID;

    previewUrlMutation({
      variables: { url, group_id: groupId },
    });
  }, [url]);

  const [previewUrlMutation, { loading, error }] = usePreviewUrlMutation({
    onError: notify.mutationError,
    onCompleted: mutation => handleCompleted(mutation),
  });

  function getContentSection() {
    if (isEmbed(values)) {
      return <div className={classes.align}>{getEmbed(values)}</div>;
    }

    const { content_object } = values;
    let title = null;
    if (content_object.url_metadata) {
      title = content_object.url_metadata.title;
    }

    return (
      <Fragment>
        <Image content={values} />
        <CardContent className={classes.cardBody}>
          <Title title={title} />
        </CardContent>
      </Fragment>
    );
  }

  function handleCompleted(mutation: PreviewUrlMutation) {
    const { is_engageable, is_shareable, content_object } = mutation.processContent;

    setFieldValue('content_object.url', content_object.url);
    setFieldValue('content_object.url_metadata', content_object.url_metadata);
    setFieldValue('content_object.tweet_metadata', content_object.tweet_metadata);
    setFieldValue('content_object.youtube_metadata', content_object.youtube_metadata);
    setFieldValue('content_object.instagram_metadata', content_object.instagram_metadata);
    setFieldValue('content_object.reddit_metadata', content_object.reddit_metadata);

    setFieldValue('is_engageable', is_engageable);
    setFieldValue('is_shareable', is_shareable);
  }

  function renderPreview() {
    return (
      <Card elevation={0} className={classes.card} square>
        <Divider />
        <CardHeader
          classes={{ root: classes.cardHeaderRoot }}
          title={<Provider content={values} />}
          action={
            onRemove && (
              <Button className={classes.button} onClick={onRemove(RemoveTypes.Url)}>
                <Trans i18nKey="posting:removePreview" />
              </Button>
            )
          }
        />
        {getContentSection()}
      </Card>
    );
  }

  function renderUnavailable() {
    return (
      <Card className={classes.card} square>
        <Divider />
        <CardHeader
          classes={{ root: classes.cardHeaderRoot }}
          action={
            onRemove && (
              <Button className={classes.button} onClick={onRemove(RemoveTypes.Url)}>
                <Trans i18nKey="posting:removePreview" />
              </Button>
            )
          }
        />
        <div className={classes.unavailable}>
          <Typography>
            <Trans i18nKey="posting:previewUnavailable" />
          </Typography>
        </div>
      </Card>
    );
  }

  if (error) {
    return renderUnavailable();
  }

  return (
    <div className={classes.placeholder}>
      {loading && <Loaders.Standard />}
      {!loading && values && renderPreview()}
    </div>
  );
};

export default Url;
